import { Button, Form, Modal } from 'react-bootstrap'
import { WebhookMilestonesFlexportTableRow } from '../interfaces/WebhookMilestonesFlexportTableRow.interfaces'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { Plus, Save, X } from 'react-bootstrap-icons'
import XSvg from '../../../common/components/svg/X-Svg'
import { MILESTONES_FLEXPORT_DEFAULT } from '../FlexportMilestones.defaults'

export interface FlexportMilestoneModalProps {
  show: boolean
  onHide?: () => void
  header?: ReactElement
  milestone?: WebhookMilestonesFlexportTableRow
  onSave?: (row: WebhookMilestonesFlexportTableRow) => Promise<void>
}

export function FlexportMilestoneModal(props: FlexportMilestoneModalProps) {
  if (!props.show) return null

  const [update, setUpdate] = useState<WebhookMilestonesFlexportTableRow>(() => {
    const milestone = props.milestone
    return milestone
      ? { ...milestone, rowKey: 'automation-config' }
      : {
          partitionKey: '',
          rowKey: 'automation-config',
          type: '',
          enableLoading: false,
          enableNotify: false,
          emails: JSON.stringify(['techteam@nethealthshops.com']),
        }
  })

  const [emails, setEmails] = useState('techteam@nethealthshops.com')

  useEffect(() => {
    if (props.show && props.milestone?.type) {
      setUpdate({ ...props.milestone, rowKey: 'automation-config' })
      setEmails(
        props.milestone.emails
          ? JSON.parse(props.milestone.emails).join(';')
          : 'techteam@nethealthshops.com'
      )
    }
  }, [props.milestone, props.show])

  const handleCheckboxChange = (field: 'enableLoading' | 'enableNotify') => {
    setUpdate(prev => ({
      ...prev,
      [field]: !prev[field],
    }))
  }

  const handleSaveClicked = useCallback(async () => {
    if (!update.type) {
      alert('Milestone type is required.')
      return
    }

    const cleanedRow: WebhookMilestonesFlexportTableRow = {
      ...update,
      partitionKey: encodeURIComponent(update.type),
      rowKey: 'automation-config',
      emails: JSON.stringify(
        emails.split(';').map(e => e.trim()).filter(Boolean)
      ),
      description: undefined
    }

    await props.onSave?.(cleanedRow)
    props.onHide?.()
  }, [update, emails, props])

  return (
    <Modal show={props.show} onHide={props.onHide} animation={false} className='modal-xl'>
      <Modal.Header closeButton>
        <span className='beacon-header'>{props.header}</span>
      </Modal.Header>
      <Modal.Body>
        <div className='beacon-header'>
          "{MILESTONES_FLEXPORT_DEFAULT.find(d => d.type === update?.type).description}"
        </div>
        <br/>
        <div>
          NOTIFY EMAILS{' '}
          <span className='beacon-label'>(Separate emails with a semi-colon. ;)</span>
          <Form.Control
            type='text'
            value={emails}
            onChange={e => setEmails(e.target.value)}
          />
        </div>
        <div
          style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '1rem' }}
        >
          <div
            title='Allows webhook events of this milestone type to be considered for data loading into Mission Control.'
            className='beacon-card'
            style={{ width: 'auto', cursor: 'pointer' }}
          >
            <Form.Check
              checked={update.enableLoading}
              onChange={() => handleCheckboxChange('enableLoading')}
            />
            <span className='beacon-label'>ENABLE LOADING</span>
          </div>
          <div
            title='Allows webhook events of this milestone type to be included in the notification emails.'
            className='beacon-card'
            style={{ width: 'auto', cursor: 'pointer' }}
          >
            <Form.Check
              checked={update.enableNotify}
              onChange={() => handleCheckboxChange('enableNotify')}
            />
            <span className='beacon-label'>ENABLE NOTIFY</span>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div>
          <Button
            onClick={handleSaveClicked}
            variant={props.milestone?.partitionKey ? 'success' : 'outline-secondary'}
          >
            {props.milestone?.partitionKey ? <Save size={16} /> : <Plus size={16} />}{' '}
            {props.milestone?.partitionKey ? 'UPDATE' : 'CREATE'}
          </Button>
          <Button variant='outline-danger' onClick={props.onHide}><X size={16} /> CANCEL</Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}
